import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { API_URL } from '../../constants';
import { axiosGetWithCallbacks, CallbackOptions } from '../../services/rest-utils';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { PageWrapper } from '../../styledComponents/wrappers';
import { NotificationManager } from 'react-notifications';
import { formatPhone } from '../../utils/helper';
import axios from '../../utils/axios';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import {
  DealerVendor,
  PaymentType,
  AchPaymentTypeDetails,
  CheckPaymentTypeDetails,
  PaypalPaymentTypeDetails,
} from '../../types/DealerVendor';

interface DealerVendorStatus {
  id: number;
  name: string;
  description: string;
  isActiveState: boolean;
}

const DealerVendorDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [dealerVendor, setDealerVendor] = useState<DealerVendor | null>(null);
  const [statuses, setStatuses] = useState<DealerVendorStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => Promise<void>;
  }>({
    open: false,
    title: '',
    message: '',
    action: async () => Promise.resolve(),
  });

  const fetchDealerVendor = useCallback(async () => {
    const callbackOptions: CallbackOptions<DealerVendor> = {
      onSuccess: data => {
        setDealerVendor(data);
        setLoading(false);
        setHasPermission(true);
      },
      catchFailure: error => {
        console.error('Error fetching dealer vendor:', error);
        if (error.response?.status === 401) {
          setHasPermission(false);
          setLoading(false);
        } else {
          NotificationManager.error('Error fetching dealer vendor details', 'Error');
          setLoading(false);
        }
      },
    };

    await axiosGetWithCallbacks<DealerVendor>(
      `${API_URL}/tradeup/dealer-vendors/${id}`,
      undefined,
      callbackOptions,
    );
  }, [id]);

  const fetchStatuses = useCallback(async () => {
    const callbackOptions: CallbackOptions<DealerVendorStatus[]> = {
      onSuccess: data => {
        setStatuses(data);
      },
      catchFailure: error => {
        console.error('Error fetching statuses:', error);
      },
    };

    await axiosGetWithCallbacks<DealerVendorStatus[]>(
      `${API_URL}/tradeup/dealer-vendor-statuses`,
      undefined,
      callbackOptions,
    );
  }, []);

  useEffect(() => {
    fetchDealerVendor();
    fetchStatuses();
  }, [fetchDealerVendor, fetchStatuses]);

  const handleStatusChange = async (newStatusId: number) => {
    if (!hasPermission) {
      NotificationManager.error(
        'You do not have permission to update dealer vendor status',
        'Error',
      );
      return;
    }

    try {
      await axios.post(`${API_URL}/tradeup/dealer-vendors/${id}/status`, {
        statusId: newStatusId,
      });
      await fetchDealerVendor();
      NotificationManager.success('Status updated successfully', 'Success');
    } catch (error) {
      console.error('Error updating status:', error);
      if (error.response?.status === 401) {
        NotificationManager.error(
          'You do not have permission to update dealer vendor status',
          'Error',
        );
      } else {
        NotificationManager.error('Error updating status', 'Error');
      }
    }
  };

  const handleResendVerification = async () => {
    if (!hasPermission) {
      NotificationManager.error(
        'You do not have permission to resend verification emails',
        'Error',
      );
      return;
    }

    try {
      if (!dealerVendor?.createdByUserId) {
        throw new Error('Creator ID not found');
      }
      await axios.post(
        `${API_URL}/tradeup/partners/users/${dealerVendor.createdByUserId}/resend-verification`,
      );
      NotificationManager.success('Verification email sent successfully', 'Success');
    } catch (error) {
      console.error('Error resending verification email:', error);
      if (error.response?.status === 401) {
        NotificationManager.error(
          'You do not have permission to resend verification emails',
          'Error',
        );
      } else {
        NotificationManager.error('Error resending verification email', 'Error');
      }
    }
  };

  const getAvailableActions = () => {
    if (!dealerVendor) return [];

    const currentStatus = dealerVendor.status.name.toLowerCase();
    const actions = [];

    if (currentStatus === 'pending') {
      actions.push(
        {
          label: 'Verify',
          color: 'success' as const,
          disabled: !dealerVendor.creatorEmailVerified,
          onClick: () => {
            const verifiedStatus = statuses.find(s => s.name.toLowerCase() === 'verified');
            if (verifiedStatus) {
              setActionDialog({
                open: true,
                title: 'Verify Dealer Vendor',
                message: dealerVendor.creatorEmailVerified
                  ? 'Are you sure you want to verify this dealer vendor? This will allow them to proceed with the setup process.'
                  : 'Cannot verify dealer vendor until their email has been verified.',
                action: async () => {
                  if (dealerVendor.creatorEmailVerified) {
                    await handleStatusChange(verifiedStatus.id);
                  }
                  setActionDialog({ ...actionDialog, open: false });
                },
              });
            }
          },
        },
        {
          label: 'Reject',
          color: 'error' as const,
          onClick: () => {
            const rejectedStatus = statuses.find(s => s.name.toLowerCase() === 'rejected');
            if (rejectedStatus) {
              setActionDialog({
                open: true,
                title: 'Reject Dealer Vendor',
                message:
                  'Are you sure you want to reject this dealer vendor? This action cannot be undone.',
                action: async () => {
                  await handleStatusChange(rejectedStatus.id);
                  setActionDialog({ ...actionDialog, open: false });
                },
              });
            }
          },
        },
      );
    } else if (currentStatus === 'verified') {
      actions.push(
        {
          label: 'Activate',
          color: 'success' as const,
          onClick: () => {
            const activeStatus = statuses.find(s => s.name.toLowerCase() === 'active');
            if (activeStatus) {
              setActionDialog({
                open: true,
                title: 'Activate Dealer Vendor',
                message:
                  'Are you sure you want to activate this dealer vendor? This will set them up in Netsuite and remove their ACH account number.',
                action: async () => {
                  await handleStatusChange(activeStatus.id);
                  setActionDialog({ ...actionDialog, open: false });
                },
              });
            }
          },
        },
        {
          label: 'Reject',
          color: 'error' as const,
          onClick: () => {
            const rejectedStatus = statuses.find(s => s.name.toLowerCase() === 'rejected');
            if (rejectedStatus) {
              setActionDialog({
                open: true,
                title: 'Reject Dealer Vendor',
                message:
                  'Are you sure you want to reject this dealer vendor? This action cannot be undone.',
                action: async () => {
                  await handleStatusChange(rejectedStatus.id);
                  setActionDialog({ ...actionDialog, open: false });
                },
              });
            }
          },
        },
      );
    }

    return actions;
  };

  const renderPaymentDetails = (dealerVendor: DealerVendor) => {
    switch (dealerVendor.paymentType) {
      case PaymentType.ACH: {
        const details = dealerVendor.paymentTypeDetails as AchPaymentTypeDetails;
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary">
                Bank Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Bank Name:</strong> {details.achBankName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Routing Number:</strong> {details.achRoutingNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Account Type:</strong> {details.achAccountType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2">
                  <strong>Account Number:</strong>{' '}
                  {details.achAccountNumber
                    ? showAccountNumber
                      ? details.achAccountNumber
                      : details.achAccountNumberMasked
                    : details.achAccountNumberMasked}
                </Typography>
                {details.achAccountNumber && (
                  <Tooltip
                    title={showAccountNumber ? 'Hide Account Number' : 'Show Account Number'}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setShowAccountNumber(!showAccountNumber)}
                      style={{ marginLeft: 8 }}
                    >
                      {showAccountNumber ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>
        );
      }
      case PaymentType.CHECK: {
        const details = dealerVendor.paymentTypeDetails as CheckPaymentTypeDetails;
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary">
                Check Details
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Payable To:</strong> {details.checkPayableTo}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Mailing Address:</strong>
              </Typography>
              <Typography variant="body2" sx={{ pl: 2 }}>
                {details.checkMailingAddress.address}
                {details.checkMailingAddress.address2 && (
                  <>
                    <br />
                    {details.checkMailingAddress.address2}
                  </>
                )}
                <br />
                {details.checkMailingAddress.city}, {details.checkMailingAddress.state}{' '}
                {details.checkMailingAddress.zip}
              </Typography>
            </Grid>
          </Grid>
        );
      }
      case PaymentType.PAYPAL: {
        const details = dealerVendor.paymentTypeDetails as PaypalPaymentTypeDetails;
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary">
                PayPal Details
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>PayPal Email:</strong> {details.paypalEmail}
              </Typography>
            </Grid>
          </Grid>
        );
      }
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageWrapper>
    );
  }

  if (!hasPermission) {
    return (
      <PageWrapper>
        <Box mb={3}>
          <Button variant="outlined" onClick={() => history.push('/dealers/vendors')}>
            Back to List
          </Button>
        </Box>
        <Card>
          <CardContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              py={4}
            >
              <Typography variant="h5" color="error" gutterBottom>
                Access Denied
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                You do not have permission to view dealer vendor details.
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Please contact your administrator if you believe this is an error.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </PageWrapper>
    );
  }

  if (!dealerVendor) {
    return (
      <PageWrapper>
        <Alert severity="error">Dealer vendor not found</Alert>
      </PageWrapper>
    );
  }

  const actions = getAvailableActions();

  return (
    <PageWrapper>
      <Box mb={3}>
        <Button variant="outlined" onClick={() => history.push('/dealers/vendors')}>
          Back to List
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">{dealerVendor.name}</Typography>
                    <Box>
                      {actions.map((action, index) => (
                        <Button
                          key={index}
                          variant="contained"
                          color={action.color}
                          onClick={action.onClick}
                          disabled={!hasPermission || action.disabled}
                          sx={{ ml: 1 }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  <Typography
                    variant="body1"
                    color={dealerVendor.status.isActiveState ? 'success.main' : 'text.secondary'}
                  >
                    {dealerVendor.status.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {dealerVendor.status.description}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Payment Type
                  </Typography>
                  <Typography variant="body1">{dealerVendor.paymentType.toUpperCase()}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Contact Information
                  </Typography>
                  <Typography variant="body1">Phone: {formatPhone(dealerVendor.phone)}</Typography>
                  <Typography variant="body1">Website: {dealerVendor.url}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Address
                  </Typography>
                  <Typography variant="body1">{dealerVendor.address}</Typography>
                  {dealerVendor.address2 && (
                    <Typography variant="body1">{dealerVendor.address2}</Typography>
                  )}
                  <Typography variant="body1">
                    {dealerVendor.city}, {dealerVendor.state} {dealerVendor.zip}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Created By
                  </Typography>
                  <Typography variant="body1">{dealerVendor.creatorName}</Typography>
                  {dealerVendor.creatorEmail && (
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {dealerVendor.creatorEmail}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="caption"
                          color={dealerVendor.creatorEmailVerified ? 'success.main' : 'error.main'}
                        >
                          {dealerVendor.creatorEmailVerified
                            ? 'Email Verified'
                            : 'Email Not Verified'}
                        </Typography>
                        {!dealerVendor.creatorEmailVerified && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleResendVerification}
                            disabled={!hasPermission}
                            sx={{ ml: 1 }}
                          >
                            Resend Verification
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    {new Date(dealerVendor.created).toLocaleString()}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Last Modified
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(dealerVendor.modified).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Information
              </Typography>
              {dealerVendor && (
                <>
                  <Typography variant="body1" gutterBottom>
                    <strong>Payment Type:</strong> {dealerVendor.paymentType.toUpperCase()}
                  </Typography>
                  {renderPaymentDetails(dealerVendor)}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ ...actionDialog, open: false })}
      >
        <DialogTitle>{actionDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{actionDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ ...actionDialog, open: false })}>Cancel</Button>
          {dealerVendor?.creatorEmailVerified && hasPermission && (
            <Button onClick={actionDialog.action} color="primary" variant="contained">
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default DealerVendorDetails;
